import { type CSSProperties } from 'react';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger' | 'warning';
}

export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
}: ConfirmDialogProps) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  const getVariantColor = () => {
    switch (variant) {
      case 'danger':
        return '#E11D48';
      case 'warning':
        return '#F59E0B';
      default:
        return '#510CFF';
    }
  };

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  };

  const dialogStyle: CSSProperties = {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    maxWidth: '500px',
    width: '100%',
    padding: '1.5rem',
  };

  const titleStyle: CSSProperties = {
    fontSize: '20px',
    fontWeight: 600,
    color: '#232730',
    margin: '0 0 1rem 0',
  };

  const messageStyle: CSSProperties = {
    fontSize: '16px',
    color: '#666B71',
    lineHeight: '1.5',
    margin: '0 0 1.5rem 0',
  };

  const buttonGroupStyle: CSSProperties = {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end',
  };

  const cancelButtonStyle: CSSProperties = {
    padding: '0.5rem 1rem',
    backgroundColor: 'white',
    color: '#666B71',
    border: '1px solid #E1E2E6',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    transition: 'all 200ms ease-in-out',
  };

  const confirmButtonStyle: CSSProperties = {
    padding: '0.5rem 1rem',
    backgroundColor: getVariantColor(),
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    transition: 'all 200ms ease-in-out',
  };

  return (
    <div
      style={backdropStyle}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
    >
      <div style={dialogStyle}>
        <h2 id="confirm-dialog-title" style={titleStyle}>
          {title}
        </h2>
        <p id="confirm-dialog-message" style={messageStyle}>
          {message}
        </p>
        <div style={buttonGroupStyle}>
          <button
            onClick={onClose}
            style={cancelButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F9FAFB';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            style={confirmButtonStyle}
            onMouseEnter={(e) => {
              const color = getVariantColor();
              e.currentTarget.style.backgroundColor = color + 'E6'; // 90% opacity
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = getVariantColor();
            }}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
